import React, { useCallback, useEffect, useState } from 'react';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import {
  FormGrid,
  ImgUploadGrid,
  InnerGrid,
  ModalHeader,
  ModalInner,
} from '@components/wrapper/Modal/ModalStyle';
import TextInput from '@components/dataEntry/Input/TextInput';
import Button from '@components/dataEntry/button/Button';
import AutoTextarea from '@components/dataEntry/textarea/AutoTextarea/index';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import CustomUploadBox from '@components/dataEntry/upload/CustomUploadBox';
import { useInputs } from '@hooks/useInputs';
import { updateStoreItem } from '@store/firebase/store/update-item';
import { IstoreItem } from '@typings/store/storeItem.interface';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import {
  deleteItemsFromInventory,
  parchaseItem,
} from '@store/firebase/store/parchase-item';
import { CollectionPath } from '@typings/collections.enum';
import { getPathKey } from '@utils/getPath';
import { ISkillItem } from '@typings/store/skill.interface';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { representativePhotocardIDState } from '@atom/photoCard';
import { log } from 'console';
import { IPhotoCard } from '@typings/photocard/photocard.interface';

type Props = {
  close: () => void;
  targetItemData: IstoreItem | ISkillItem | any;
  photocardData?: IPhotoCard;
};
function MakeItemModal({ close, targetItemData, photocardData }: Props) {
  const [addLoading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const representativeId = useRecoilValue(representativePhotocardIDState);
  const routeId = getPathKey(2);
  const photocardID = photocardData?.id || routeId || representativeId; // 대표 포토카드 ID 사용
  const currentUid = useRecoilValue(currentUserIDState);

  const [{ title, description, contents }, handleChange, reset] = useInputs({
    title: '제목 없음',
    description: '',
    contents: '',
  });

  const handleUpdateItem = useCallback(async () => {
    setLoading(true);

    const newData: IstoreItem = {
      title,
      order: 0,
      description,
      contents,
      functionType: ITEM_FUNCTION_TPYE.DEFAULT,
      eventOn: false,
      price: 0,
      bedgeText: '',
      isShow: false,
      statusId: '',
      multiple: [],
      giftingCharID: '',
      giftMessage: '',
    };

    // 아이템 등록 후
    const newItemID = await updateStoreItem({
      data: newData,
      hashtag: [],
      prevHashtag: [],
      docID: '',
      fileList: {
        selectedFile,
        prevImgURL: '',
      },
    });

    //  ------------------------------------------------
    // 차감할 아이템과 선물할 아이템이 다르기 때문에 분리
    //  ------------------------------------------------

    if (!photocardID) {
      alert('대표 캐릭터를 먼저 선택/설정해주세요.');
      setLoading(false);
      return;
    }

    // 기존 인벤토리에서 재료 차감
    const inventoryPath = `${photocardData?.path}/${photocardID}/inventory`;
    await deleteItemsFromInventory({
      data: targetItemData,
      amount: 1,
      collectionPath: inventoryPath,
    });

    // 새로 만든 아이템을 바로 나 자신이 구매
    await parchaseItem({
      collectionPath: photocardData?.path || CollectionPath.PHOTOCARD,
      amount: 1,
      data: {
        ...newData,
        id: newItemID,
      },
      buyerID: currentUid || '',
      photocardID: photocardID,
    });

    setLoading(false);
    close();
  }, [title, description, contents, selectedFile, photocardID, currentUid]);

  useEffect(() => {
    setDisabled(!title);
  }, [title]);

  return (
    <ModalWrapper outsideClick={() => { }} contentStyle={{ width: 600 }}>
      <ModalHeader>
        <CloseBtn onClick={close} />
        <div style={{ textAlign: 'left' }}>
          <h6>아이템 만들기</h6>
        </div>
        <Button
          onClick={handleUpdateItem}
          pending={addLoading}
          disabled={disabled}
        >
          저장하기
        </Button>
      </ModalHeader>
      <ModalInner>
        <InnerGrid>
          <ImgUploadGrid>
            <CustomUploadBox
              id="make-item-thumb"
              setSelectedFile={setSelectedFile}
              prevImgURL={''}
            />
          </ImgUploadGrid>

          <FormGrid>
            <TextInput
              label="제목"
              placeholder="20자 내 텍스트 입력..."
              maxLength={20}
              name={'title'}
              value={title}
              onChange={handleChange}
              required
            />
            <TextInput
              label="간략 설명"
              placeholder="입력..."
              name={'description'}
              value={description}
              onChange={handleChange}
              maxLength={5000}
            />
            <AutoTextarea
              label="설명"
              placeholder="입력..."
              name={'contents'}
              value={contents}
              onChange={handleChange}
              minRows={4}
              maxRows={5}
              maxLength={5000}
            />
          </FormGrid>
        </InnerGrid>
      </ModalInner>
    </ModalWrapper>
  );
}

export default MakeItemModal;
